import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WatsonxOrchestrateTokenService } from '../../services/watsonx-orchestrate-token.service';

interface IWatsonxOrchestrateInstance {
  readonly changeView?: (view: string) => void;
  readonly on?: (eventName: string, handler: (event: any, instance?: IWatsonxOrchestrateInstance) => void) => void;
  readonly off?: (eventName: string, handler: (event: any, instance?: IWatsonxOrchestrateInstance) => void) => void;
  readonly send?: (payload: unknown) => void;
  readonly updateIdentityToken?: (token: string) => void;
  readonly updateAuthToken?: (token: string) => void;
}

interface IWatsonxOrchestrateConfiguration {
  readonly orchestrationID: string;
  readonly hostURL: string;
  readonly token: string;
  readonly showLauncher: boolean;
  readonly deploymentPlatform?: string;
  readonly crn?: string;
  readonly rootElementID?: string;
  readonly chatOptions: {
    readonly agentId: string;
    readonly agentEnvironmentId: string;
    readonly onLoad: (instance: IWatsonxOrchestrateInstance) => void;
  };
}

declare global {
  interface Window {
    wxOConfiguration?: IWatsonxOrchestrateConfiguration;
    wxoLoader?: {
      readonly init: () => void;
    };
  }
}

@Component({
  selector: 'app-emese-chatbot',
  templateUrl: './emese-chatbot.component.html',
  styleUrls: ['./emese-chatbot.component.scss'],
})
export class EmeseChatbotComponent implements OnInit, OnDestroy {
  @Input() accentColor = '#38BA9B';
  @Input() launcherImageSrc = 'https://vh-public-bucket.s3.eu-de.cloud-object-storage.appdomain.cloud/avatarEmese1.png';
  @Input() launcherImageAlt = 'Assistant';
  @Input() launcherText = 'I\'m Emese Hungary\'s first tourism chatbot.\nHow can I help you?';

  isLauncherHidden = false;

  get launcherAriaLabel(): string {
    const text = this.launcherText?.replace(/\n+/g, ' ');
    return text?.replace(/\s+/g, ' ').trim() || 'Open chat';
  }

  private readonly scriptId = 'watsonx-orchestrate-chat-loader';
  private chatInstance?: IWatsonxOrchestrateInstance;
  private initializationPromise?: Promise<void>;
  private refreshTokenPromise?: Promise<void>;
  private pendingOpenRequest = false;
  private readonly viewChangeHandler = (event: any): void => this.handleViewChange(event);
  private readonly receiveHandler = (event: any): void => this.handleReceive(event);
  private readonly identityTokenExpiredHandler = (_event: any, instance?: IWatsonxOrchestrateInstance): void => {
    void this.refreshIdentityToken(instance);
  };

  constructor(
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
    private readonly tokenService: WatsonxOrchestrateTokenService,
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.host.nativeElement.style.setProperty('--emese-chatbot-accent', this.accentColor);
    if (!this.isOrchestrateConfigurationValid()) {
      this.isLauncherHidden = true;
      return;
    }

    void this.initializeChat();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromEvent('view:change', this.viewChangeHandler);
    this.unsubscribeFromEvent('receive', this.receiveHandler);
    this.unsubscribeFromEvent('identityTokenExpired', this.identityTokenExpiredHandler);
    this.pendingOpenRequest = false;
    this.resetGlobalConfiguration();
  }

  openChat(): void {
    if (!this.chatInstance) {
      this.pendingOpenRequest = true;
      void this.initializeChat();
      return;
    }

    this.openMainWindow();
  }

  private initializeChat(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    const initTask = this.ngZone.runOutsideAngular(async () => {
      try {
        const token = await firstValueFrom(this.tokenService.getIdentityToken$());
        this.configureOrchestrate(token);
        await this.loadOrchestrateScript();
      } catch (error) {
        console.error('Watsonx Orchestrate chat initialization failed', error);
      }
    });

    this.initializationPromise = initTask.finally(() => {
      if (!this.chatInstance) {
        this.initializationPromise = undefined;
      }
    });

    return this.initializationPromise;
  }

  private onChatLoad(instance: IWatsonxOrchestrateInstance): void {
    this.chatInstance = instance;

    this.subscribeToEvent('view:change', this.viewChangeHandler);
    this.subscribeToEvent('receive', this.receiveHandler);
    this.subscribeToEvent('identityTokenExpired', this.identityTokenExpiredHandler);

    if (this.pendingOpenRequest) {
      this.openMainWindow();
    }
  }

  private configureOrchestrate(token: string): void {
    const orchestrate = environment.watsonxOrchestrate;
    if (!orchestrate) {
      throw new Error('Watsonx Orchestrate configuration is not available.');
    }

    window.wxOConfiguration = {
      orchestrationID: orchestrate.orchestrationID,
      hostURL: orchestrate.hostURL,
      token,
      showLauncher: false,
      deploymentPlatform: orchestrate.deploymentPlatform,
      crn: orchestrate.crn,
      rootElementID: orchestrate.rootElementID,
      chatOptions: {
        agentId: orchestrate.agentId,
        agentEnvironmentId: orchestrate.agentEnvironmentId,
        onLoad: (instance: IWatsonxOrchestrateInstance) => this.onChatLoad(instance),
      },
    };
  }

  private loadOrchestrateScript(): Promise<void> {
    if (window.wxoLoader?.init) {
      window.wxoLoader.init();
      return Promise.resolve();
    }

    const existingScript = document.getElementById(this.scriptId) as HTMLScriptElement | null;
    if (existingScript) {
      return this.waitForLoaderAndInit();
    }

    const hostURL = environment.watsonxOrchestrate?.hostURL?.trim();
    if (!hostURL) {
      return Promise.reject(new Error('Watsonx hostURL is missing from environment configuration.'));
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = this.scriptId;
      script.src = `${hostURL}/wxochat/wxoLoader.js?embed=true`;
      script.async = true;
      script.onload = (): void => {
        this.waitForLoaderAndInit().then(resolve).catch(reject);
      };
      script.onerror = (event): void => reject(event);
      document.head.appendChild(script);
    });
  }

  private waitForLoaderAndInit(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50;
      const interval = window.setInterval(() => {
        attempts += 1;
        if (window.wxoLoader?.init) {
          window.clearInterval(interval);
          window.wxoLoader.init();
          resolve();
          return;
        }

        if (attempts >= maxAttempts) {
          window.clearInterval(interval);
          reject(new Error('Watsonx loader did not become available in time.'));
        }
      }, 100);
    });
  }

  private openMainWindow(): void {
    if (typeof this.chatInstance?.changeView === 'function') {
      this.chatInstance.changeView('mainWindow');
    }
    this.pendingOpenRequest = false;
  }

  private subscribeToEvent(
    eventName: string,
    handler: (event: any, instance?: IWatsonxOrchestrateInstance) => void,
  ): void {
    if (typeof this.chatInstance?.on !== 'function') {
      return;
    }

    try {
      this.chatInstance.on(eventName, handler);
    } catch (error) {
      try {
        (this.chatInstance.on as any)({ type: eventName, handler });
      } catch {
        console.error(`Failed to subscribe to Watsonx event: ${eventName}`, error);
      }
    }
  }

  private unsubscribeFromEvent(
    eventName: string,
    handler: (event: any, instance?: IWatsonxOrchestrateInstance) => void,
  ): void {
    if (typeof this.chatInstance?.off !== 'function') {
      return;
    }

    try {
      this.chatInstance.off(eventName, handler);
    } catch (error) {
      try {
        (this.chatInstance.off as any)({ type: eventName, handler });
      } catch {
        console.error(`Failed to unsubscribe from Watsonx event: ${eventName}`, error);
      }
    }
  }

  private isOrchestrateConfigurationValid(): boolean {
    const orchestrate = environment.watsonxOrchestrate;
    if (!orchestrate?.enabled) {
      return false;
    }

    const requiredValues = [
      orchestrate.hostURL,
      orchestrate.orchestrationID,
      orchestrate.agentId,
      orchestrate.agentEnvironmentId,
      orchestrate.tokenEndpoint,
    ];

    return requiredValues.every((value) => !!value?.trim());
  }

  private handleViewChange(event: any): void {
    const shouldHide = !!(event?.newViewState?.mainWindow ?? event?.viewState?.mainWindow);
    if (shouldHide === this.isLauncherHidden) {
      return;
    }

    this.ngZone.run(() => {
      this.isLauncherHidden = shouldHide;
      this.cdr.markForCheck();
    });
  }

  private handleReceive(event: any): void {
    const items = event?.data?.output?.generic ?? event?.message?.content;
    if (!Array.isArray(items)) {
      return;
    }

    items.forEach((item: any) => {
      if (item?.user_defined?.user_defined_type === 'share_location') {
        this.requestLocation();
      }
    });
  }

  private requestLocation(): void {
    if (!navigator?.geolocation) {
      this.sendAssistantMessage('Location services are unavailable on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => this.handleLocationSuccess(position),
      (error) => this.handleLocationError(error),
    );
  }

  private handleLocationSuccess(position: GeolocationPosition): void {
    if (!this.chatInstance?.send) {
      return;
    }

    this.chatInstance.send({
      input: {
        text: 'Ok I\'ve shared my location.',
      },
      context: {
        skills: {
          'actions skill': {
            skill_variables: {
              location_lat: position.coords.latitude,
              location_lon: position.coords.longitude,
            },
          },
        },
      },
    });
  }

  private handleLocationError(error: GeolocationPositionError): void {
    if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
      this.sendAssistantMessage('I don\'t want to share my location at this time.');
      return;
    }

    if (error.code === GeolocationPositionError.POSITION_UNAVAILABLE) {
      this.sendAssistantMessage('The browser encountered an error sharing my location.');
      return;
    }

    this.sendAssistantMessage('There was an error sharing my location.');
  }

  private sendAssistantMessage(text: string): void {
    if (!this.chatInstance?.send) {
      return;
    }

    this.chatInstance.send({ input: { text } });
  }

  private refreshIdentityToken(instance?: IWatsonxOrchestrateInstance): Promise<void> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = this.ngZone.runOutsideAngular(async () => {
      try {
        const token = await firstValueFrom(this.tokenService.getIdentityToken$());
        const target = instance || this.chatInstance;
        if (typeof target?.updateIdentityToken === 'function') {
          target.updateIdentityToken(token);
          return;
        }

        if (typeof target?.updateAuthToken === 'function') {
          target.updateAuthToken(token);
          return;
        }

        console.warn('Watsonx chat instance does not support identity token updates.');
      } catch (error) {
        console.error('Watsonx identity token refresh failed', error);
      } finally {
        this.refreshTokenPromise = undefined;
      }
    });

    return this.refreshTokenPromise;
  }

  private resetGlobalConfiguration(): void {
    if (typeof window === 'undefined') {
      return;
    }

    delete window.wxOConfiguration;
  }
}
